import React, { ReactNode } from 'react';
import { Logger } from '@cvent/nucleus-logging';

import { Error as ErrorComponent, ErrorProps } from '@pages/_error';
import { HardError } from '@components/errors/HardError';

const LOG = new Logger('ErrorBoundary');

interface Props {
  children: ReactNode;
}
interface State extends ErrorProps {
  hasError: boolean;
}
/**
 * A component that catches JavaScript errors that occur in child components, logs the error and redirects to the error page
 * See https://reactjs.org/docs/error-boundaries.html
 */
export class ErrorBoundary extends React.Component {
  constructor(props: Props) {
    super(props);

    this.state = { hasError: false, errorType: null, errorDetail: null };
  }

  public static getDerivedStateFromError(error: Error) {
    const state: State = { hasError: true };

    if (error.name === 'HardError') {
      const hardError = error as HardError;
      state.errorDetail = hardError.errorCtx;

      switch (hardError.errorCtx.statusCode) {
        case 404:
          state.errorType = 'ERROR_404';
          break;
        default:
          state.errorType = 'SIMPLE_ERROR';
      }
    }

    return state;
  }

  public componentDidCatch(error: Error): void {
    LOG.error('Caught error in boundary: ', error);
  }

  public render() {
    const localState = this.state as State;

    if (localState.hasError) {
      return <ErrorComponent errorType={localState.errorType} errorDetail={localState.errorDetail} />;
    }
    return (this.props as Props).children;
  }
}

export default ErrorBoundary;
